import React, { Fragment, useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import Navbar from "./Navbar";
import TwoColWithSidebar, { Sidebar, Main } from "./layouts/TwoColWithSidebar";
import SidebarTips from "./layouts/SidebarTips";
import SidebarSection from "./layouts/SidebarSection";
import SidebarHeader from "./layouts/SidebarHeader";
import Video from "./layouts/Video";
import Button from "./Button";
import Modal from "./Modal";
import Notification, { addNotification } from "./Notification";
import SLThumbnail from "./SLThumbnail";
// import Warning from "./Warning";
import Spinner from "./Spinner";
import { Uploader } from "./utils/uploader";
import { newSignLanguageHook } from "./utils/slackWebhooks";
import firebase from "firebase/compat/app";
import { db } from "./auth/firebase";

import { parseEqpField, trackStatus } from "./utils/customField";
import {
  bcCMSCreateVideo,
  bcDeleteVideo,
  bcMutateEqpField,
  getBcVideo,
} from "./utils/brightcoveRequest";
import { t } from "../i18n/language";
import Select from "./Select";

/** Label a new sign language track gets when its account has not set its own.
 *  Deliberately generic — the real abbreviation is national. */
const DEFAULT_SIGN_LABEL = "SL";

export default function SignLanguage() {
  const [video, setVideo] = useState(false);
  const [notFound, setNotFound] = useState(false);
  // Guarded: this runs on every render, so an unreadable field used to throw
  // during render and take the whole page with it.
  const eqpField = parseEqpField(video, "SignLanguage");

  //  const [uploadTaskRef, setUploadTaskRef] = useState();
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState("");
  const [uploaded, setUploaded] = useState(false);
  const [importError, setImportError] = useState(false);
  const [importErrorMessage, setImportErrorMessage] = useState("");
  const [onCompleteModal, setOnCompleteModal] = useState(false);
  const [videoPlayer, setVideoPlayer] = useState(false);
  const [videoPosition, setVideoPosition] = useState([70, 35, 25]);
  const [signVideos, setSignVideos] = useState({});
  const [uploadingSignId, setUploadingSignId] = useState(null);
  const [addingSl, setAddingSl] = useState(false);
  const [deleteConfirmSignId, setDeleteConfirmSignId] = useState(null);
  const [deletingSignId, setDeletingSignId] = useState(null);
  // Fresh read from bc_accounts — Redux's selectedAccount is a snapshot from
  // login/account-switch time and doesn't reflect later admin edits.
  const [ingestProfile, setIngestProfile] = useState(null);
  // Account-wide default for the label on a new sign language track. The
  // abbreviation is national - BSL in England, ISL in Ireland, STS in Sweden -
  // so "SL" is only the fallback when an account has not set one.
  const [signLanguageLabel, setSignLanguageLabel] = useState(
    DEFAULT_SIGN_LABEL
  );
  // Handle for the currently-running Uploader, so the Cancel button can
  // terminate workers and abort pending register/manifest XHRs. Cleared
  // whenever an upload finishes, errors, or is cancelled.
  const uploadCancelRef = useRef(null);

  const videoId = useParams().id;

  const user = useSelector((state) => state.user);
  const selectedAccount = useSelector((state) => state.user.selectedAccount);

  const resetUploader = () => {
    setProgress(0);
    setFileName("");
    setUploaded(false);
    setUploadingSignId(null);
    setImportError(false);
    setImportErrorMessage("");
    setOnCompleteModal(false);
  };

  // Compact toast body for uploader failures. Payload shape:
  // { stage: "register"|"chunk"|"manifest", status: number, response: string }
  const uploaderErrorBody = (err) => {
    const stage = err?.stage || "upload";
    const status = err?.status;
    const detail = err?.response ? ` — ${String(err.response).slice(0, 80)}` : "";
    return `${stage} (${status ?? "?"})${detail}`;
  };

  const handleCancelUpload = () => {
    if (uploadCancelRef.current) {
      // Client-side abort only. Chunks already in GCS become orphans; the
      // uploader GCF's cleanup pass will reap them later (see TODO.md).
      try { uploadCancelRef.current.cancel(); } catch (e) { console.error(e); }
      uploadCancelRef.current = null;
    }
    resetUploader();
  };

  const handleAddSignLanguage = async () => {
    if (addingSl || !video?.id) return;
    setAddingSl(true);
    // Tracked outside the try so the failure path can report which Brightcove
    // video was left behind: if creation succeeds and a later write throws, the
    // placeholder exists in BC with no row on the parent — invisible here, and
    // the operator has no way to find it without this id.
    let createdSlVideoId = null;
    try {
      const createVideoRes = await bcCMSCreateVideo(
        selectedAccount,
        `SL video for ${video?.name ?? videoId}`,
        {
          description: `Parent BC video ID: ${videoId}`,
          tags: ["equalplay-alpha"],
        }
      );
      const slBcVideoId = createVideoRes.data.id;
      createdSlVideoId = slBcVideoId;

      // Reverse-lookup on the new SL video itself.
      await bcMutateEqpField({
        accountId: selectedAccount.account_id,
        videoId: slBcVideoId,
        mutate: (current) => ({
          ...current,
          is_sign: true,
          parent: videoId,
        }),
        notify: false,
      });

      // Add the sign row to the parent's equalplay field with status "new".
      // (Uses bcMutateEqpField rather than bcCustomFieldUpdate because the latter
      // keys existing entries by `lang` — sign rows have no `lang`, so every existing
      // row would match and the "append" would silently overwrite instead.)
      await bcMutateEqpField({
        accountId: selectedAccount.account_id,
        videoId,
        mutate: (current) => ({
          ...current,
          sign: [
            ...(current.sign ?? []),
            {
              id: slBcVideoId,
              label: signLanguageLabel,
              status: trackStatus.new,
            },
          ],
        }),
        notify: false,
      });

      // Refresh the parent so the new row appears.
      const freshParent = await getBcVideo({
        accountId: selectedAccount.account_id,
        videoId,
      });
      if (freshParent) setVideo(freshParent);
    } catch (err) {
      console.error("Failed to add SL placeholder", err);
      addNotification(
        t("Could not add sign language"),
        createdSlVideoId
          ? `${t(
              "A placeholder was created in Brightcove but could not be linked. Please remove video"
            )} ${createdSlVideoId} ${t("in Brightcove Studio")}.`
          : err?.message || t("Please try again."),
        "bg-red-600"
      );
    } finally {
      setAddingSl(false);
    }
  };

  const handleUploadForSign = (signId, file) => {
    if (uploadingSignId) return;
    setUploadingSignId(signId);
    setFileName(file.name);
    setProgress(0);
    setUploaded(false);
    setImportError(false);
    setImportErrorMessage("");

    const Upload = new Uploader(
      "https://europe-west1-equal-play.cloudfunctions.net/uploader"
    );
    uploadCancelRef.current = Upload(
      file,
      { uid: user.uid, bcid: selectedAccount.account_id },
      (p) => {
        setProgress(p.percent);
      },
      async (complete) => {
        // Flip this row's status from "new" → "processing".
        await updateEqpField((current) => ({
          ...current,
          sign: (current.sign ?? []).map((s) =>
            s.id === signId
              ? { ...s, status: trackStatus.processing }
              : s
          ),
        }));

        // Now that the message goes via notifySignLanguageUpload, Slack's own
        // answer reaches us and a failure is real rather than guesswork — so
        // say so. The footage is safely uploaded either way; what's lost is
        // the team being told, which the user can cover with an email.
        try {
          await newSignLanguageHook(
            user,
            videoId,
            complete.url,
            complete.id,
            video?.name
          );
        } catch (err) {
          console.error("Slack notification failed", err?.message);
          addNotification(
            t("Uploaded, but we couldn't notify the team"),
            t(
              "Your file is safely uploaded. Please email us so we know it's waiting."
            ),
            "bg-red-600"
          );
        }

        uploadCancelRef.current = null;
        setUploaded(true);
        setOnCompleteModal(true);
      },
      (err) => {
        console.error("Chroma upload failed", err);
        addNotification(
          t("Upload failed"),
          uploaderErrorBody(err),
          "bg-red-600"
        );
        uploadCancelRef.current = null;
        resetUploader();
      },
      // Traceability: without this the uploads doc records only uid/bcid and a
      // filename, so an upload can never be tied back to the placeholder it was
      // for. Stored verbatim by the uploader GCF.
      {
        type: "sign_language_raw",
        accountId: selectedAccount.account_id,
        parentVideoId: videoId,
        parentName: video?.name ?? null,
        signId,
      }
    );
  };

  const handleDeleteSign = async (signId) => {
    if (!signId || deletingSignId) return;
    // Safety: only delete rows that are still in "new" status. Anything past that
    // has content and must be handled through a different (future) flow.
    const target = eqpField?.sign?.find((s) => s.id === signId);
    if (!target || target.status !== trackStatus.new) {
      console.warn("Refusing to delete: row not found or not in 'new' status");
      return;
    }
    // Extra safety: if we have BC data for the SL video, verify it carries the
    // equalplay-alpha tag we assigned on creation. Guards against calling delete
    // on a video we didn't create (belt-and-braces on top of the status check).
    const bcSl = signVideos?.[signId];
    if (
      bcSl &&
      Array.isArray(bcSl.tags) &&
      !bcSl.tags.includes("equalplay-alpha")
    ) {
      console.error(
        "Refusing to delete: BC video is not tagged equalplay-alpha",
        signId
      );
      return;
    }

    setDeletingSignId(signId);
    try {
      // Delete the BC placeholder first — if this fails, we leave the parent's
      // sign row alone so the user can retry.
      await bcDeleteVideo(selectedAccount, signId);

      // Drop the row from the parent's equalplay.sign array.
      await bcMutateEqpField({
        accountId: selectedAccount.account_id,
        videoId,
        mutate: (current) => ({
          ...current,
          sign: (current.sign ?? []).filter((s) => s.id !== signId),
        }),
        notify: false,
      });

      // Refresh the parent so the row disappears from the list.
      const freshParent = await getBcVideo({
        accountId: selectedAccount.account_id,
        videoId,
      });
      if (freshParent) setVideo(freshParent);
    } catch (err) {
      console.error("Failed to delete SL placeholder", err);
    } finally {
      setDeletingSignId(null);
      setDeleteConfirmSignId(null);
    }
  };

  const handleIngestForSign = (signId, file) => {
    if (uploadingSignId) return;
    const profile = ingestProfile;
    // Client-side hard gate. The `signLanguageIngests` trigger also validates
    // this — belt and braces so we don't hit the network on a doomed request.
    if (!profile) {
      console.warn("Ingest blocked: account has no signLanguageProfile");
      return;
    }
    setUploadingSignId(signId);
    setFileName(file.name);
    setProgress(0);
    setUploaded(false);
    setImportError(false);
    setImportErrorMessage("");

    const Upload = new Uploader(
      "https://europe-west1-equal-play.cloudfunctions.net/uploader"
    );
    uploadCancelRef.current = Upload(
      file,
      { uid: user.uid, bcid: selectedAccount.account_id },
      (p) => {
        setProgress(p.percent);
      },
      async (complete) => {
        try {
          // Ensure the parent's sign row is 'processing' when the ingest
          // callback fires — that's the state it expects to flip to 'draft'.
          await updateEqpField((current) => ({
            ...current,
            sign: (current.sign ?? []).map((s) =>
              s.id === signId
                ? { ...s, status: trackStatus.processing }
                : s
            ),
          }));

          await db.collection("signLanguageIngests").add({
            accountId: selectedAccount.account_id,
            videoId: signId,
            parentVideoId: videoId,
            url: complete.url,
            profile,
            uploaderUid: user.uid,
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          });

          uploadCancelRef.current = null;
          setUploaded(true);
          setOnCompleteModal(true);
        } catch (err) {
          console.error("Failed to write signLanguageIngests doc", err);
          setImportError(true);
          setImportErrorMessage(err.message || "Failed to start ingest");
        }
      },
      (err) => {
        console.error("Ingest upload failed", err);
        addNotification(
          t("Upload failed"),
          uploaderErrorBody(err),
          "bg-red-600"
        );
        uploadCancelRef.current = null;
        resetUploader();
      },
      {
        type: "sign_language_processed",
        accountId: selectedAccount.account_id,
        parentVideoId: videoId,
        parentName: video?.name ?? null,
        signId,
      }
    );
  };

  // For SL rows whose BC video is gone (deleted directly in BC Studio, etc.).
  // No BC call needed — the video's already gone; just filter the row out of the
  // parent's sign array.
  const handlePurgeMissingSign = async (signId) => {
    if (!signId) return;
    try {
      await bcMutateEqpField({
        accountId: selectedAccount.account_id,
        videoId,
        mutate: (current) => ({
          ...current,
          sign: (current.sign ?? []).filter((s) => s.id !== signId),
        }),
        notify: false,
      });
      const freshParent = await getBcVideo({
        accountId: selectedAccount.account_id,
        videoId,
      });
      if (freshParent) setVideo(freshParent);
    } catch (err) {
      console.error("Failed to purge missing sign", err);
    }
  };

  const updateStartPosition = () =>
    updateEqpField((current) => ({
      ...current,
      sign: (current.sign ?? []).map((s) => ({ ...s, pos: videoPosition })),
    }));

  const updateEqpField = async (mutate) => {
    try {
      const response = await bcMutateEqpField({
        accountId: video.account_id,
        videoId: video.id,
        mutate,
      });
      if (response.status === 200) {
        setVideo(response.data);
      } else {
        console.warn("Error updating eqpfield", response.status);
      }
    } catch (err) {
      console.warn("Error updating eqpfield", err?.message);
    }
  };

  useEffect(() => {
    if (selectedAccount) {
      getBcVideo({ accountId: selectedAccount.account_id, videoId })
        .then((video) => {
          setVideo(video);
        })
        .catch((err) => {
          console.log(err);
          setNotFound(true);
        });
    }
    // eslint-disable-next-line
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount?.account_id) return;
    db.collection("bc_accounts")
      .doc(String(selectedAccount.account_id))
      .get()
      .then((doc) => {
        const data = doc.exists ? doc.data() : {};
        setIngestProfile(data.signLanguageProfile || null);
        setSignLanguageLabel(data.signLanguageLabel || DEFAULT_SIGN_LABEL);
      })
      .catch((err) => {
        console.error("Failed to load ingest profile", err);
        setIngestProfile(null);
        setSignLanguageLabel(DEFAULT_SIGN_LABEL);
      });
  }, [selectedAccount?.account_id]);

  // Rehydrate the position display from the stored eqpField whenever the video is (re)loaded.
  useEffect(() => {
    if (!eqpField || !Array.isArray(eqpField.sign)) return;
    const stored = eqpField.sign.find((s) => Array.isArray(s.pos))?.pos;
    if (stored && stored.length === 3) {
      setVideoPosition(stored);
    }
    // eslint-disable-next-line
  }, [video]);

  // Fetch each SL video's own BC doc, and self-heal missing is_sign/parent on the way.
  useEffect(() => {
    if (!selectedAccount?.account_id || !video?.id) return;
    const parentId = video.id;
    const parentAccountId = selectedAccount.account_id;
    const signRows = Array.isArray(eqpField?.sign) ? eqpField.sign : [];
    if (signRows.length === 0) {
      setSignVideos({});
      return;
    }

    let cancelled = false;

    Promise.all(
      signRows.map((sl) =>
        getBcVideo({ accountId: parentAccountId, videoId: sl.id })
          .then(async (bcVideo) => {
            if (!bcVideo) return { id: sl.id, __missing: true };
            const parsed = parseEqpField(bcVideo, "SignLanguage backfill");
            const needsBackfill =
              !parsed || !parsed.is_sign || parsed.parent !== parentId;
            if (needsBackfill) {
              await bcMutateEqpField({
                accountId: parentAccountId,
                videoId: bcVideo.id,
                mutate: (current) => ({
                  ...current,
                  is_sign: true,
                  parent: parentId,
                }),
                notify: false,
              }).catch((err) => {
                console.warn(
                  `Backfill of is_sign/parent failed for SL video ${bcVideo.id}`,
                  err?.message
                );
              });
            }
            return bcVideo;
          })
          .catch((err) => {
            console.warn(`Could not fetch SL video ${sl.id}`, err?.message);
            return { id: sl.id, __missing: true };
          })
      )
    ).then((results) => {
      if (cancelled) return;
      const byId = {};
      results.forEach((v) => {
        if (v?.id) byId[v.id] = v;
      });
      setSignVideos(byId);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [selectedAccount?.account_id, video?.id, eqpField?.sign?.length]);

  useEffect(() => {
    const handleSizeChange = (el, data) => {
      function normalize(number) {
        return Math.round(number * 100) / 100;
      }
      const { left, top, width } = data;
      setVideoPosition([normalize(left), normalize(top), normalize(width)]);
    };

    if (videoPlayer && videoPlayer.equalplay) {
      // console.log("Videoplayer loaded, time to interact");
      videoPlayer.equalplay.on("sign_resize", handleSizeChange);
    }

    return () => {
      if (videoPlayer && videoPlayer.equalplay) {
        // console.log("Videoplayer loaded, time to interact");
        videoPlayer.equalplay.off("sign_resize", handleSizeChange);
      }
    };
  }, [videoPlayer]);

  return (
    <Fragment>
      <Navbar />
      <Notification />
      <TwoColWithSidebar>
        <Sidebar>
          <SidebarSection>
            <SidebarHeader text={t("Tips & tricks")} icon="tips" />
            <SidebarTips type="sl" />
          </SidebarSection>
        </Sidebar>
        <Main>
          <h1 className="text-xl font-semibold">{t("Sign language")}</h1>
          {!video && !notFound && (
            <div className="mt-4 flex">
              <Spinner />
            </div>
          )}
          {!video && notFound && `${t("Video not found")} / ${videoId}`}

          {video && (
            <div className="mt-4">
              <div className="flex flex-col gap-2 w-full">
                <div className="flex items-center gap-3">
                  <Button
                    text={t("Open in BC")}
                    iconBefore="publish"
                    colour="white"
                    size="small"
                    onClick={() =>
                      window.open(
                        `https://studio.brightcove.com/products/videocloud/media/videos/${video.id}`,
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                  />
                  <h2 className="text-gray-600">{video.name}</h2>
                </div>
                <Video
                  videoId={videoId}
                  onReady={(videoPlayer) => {
                    setVideoPlayer(videoPlayer);
                  }}
                  width="w-full"
                />
                <div className="flex flex-col px-4 py-2 bg-white border border-gray-200 rounded">
                  <h3 className="font-semibold first-letter:uppercase text-gray-600 border-b border-gray-200 py-1">
                    {t("interpreter start position")}
                  </h3>
                  <div className="flex text-sm gap-4">
                    <div className="min-w-32">
                      Left{" "}
                      <span className="font-semibold">{videoPosition[0]}%</span>
                    </div>
                    <div className="min-w-32">
                      Top{" "}
                      <span className="font-semibold">{videoPosition[1]}%</span>
                    </div>
                    <div className="min-w-32">
                      Width{" "}
                      <span className="font-semibold">{videoPosition[2]}%</span>
                    </div>
                  </div>
                  <div className="flex justify-end items-center">
                    <Button
                      text={t("set position")}
                      onClick={updateStartPosition}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-gray-600">{t("Sign languages")}</h2>
                  {addingSl ? (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Spinner />
                      <span>{t("Adding sign language...")}</span>
                    </div>
                  ) : (
                    <Button
                      text={t("Add Sign Language")}
                      colour="white"
                      iconBefore="add"
                      onClick={handleAddSignLanguage}
                      disabled={!!uploadingSignId}
                    />
                  )}
                </div>
                <SignLanguageStatus
                  eqpField={eqpField}
                  updateEqpField={updateEqpField}
                  signVideos={signVideos}
                  parentDuration={video.duration}
                  uploadingSignId={uploadingSignId}
                  onUploadForSign={handleUploadForSign}
                  onDeleteSign={setDeleteConfirmSignId}
                  onPurgeMissingSign={handlePurgeMissingSign}
                  onIngestForSign={handleIngestForSign}
                  onCancelUpload={handleCancelUpload}
                  canIngest={!!user?.is_manager}
                  hasIngestProfile={!!ingestProfile}
                  progress={progress}
                  fileName={fileName}
                  uploaded={uploaded}
                  importError={importError}
                  importErrorMessage={importErrorMessage}
                />
              </div>
            </div>
          )}
        </Main>
      </TwoColWithSidebar>

      {/* UPLOAD COMPLETE MODAL */}
      <Modal
        title={t("We'll take it from here")}
        acceptButtonText={t("Okay")}
        onAccept={resetUploader}
        onClose={resetUploader}
        show={onCompleteModal}
        width="w-88"
      >
        {t(
          "We will let you know via email when it's time to check back and approve this final video."
        )}
      </Modal>

      {/* DELETE PLACEHOLDER CONFIRMATION */}
      <Modal
        title={t("Delete this placeholder?")}
        acceptButtonText={t("Delete")}
        onAccept={() => handleDeleteSign(deleteConfirmSignId)}
        onClose={() => setDeleteConfirmSignId(null)}
        show={!!deleteConfirmSignId}
        working={!!deletingSignId}
        cancelDisabled={false}
        width="w-88"
      >
        {t(
          "This will remove the placeholder from Brightcove and drop the row from this video. Only allowed for placeholders with no content yet."
        )}
      </Modal>
    </Fragment>
  );
}

const formatDate = (iso) => {
  if (!iso) return "…";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
};

const formatDuration = (ms) => {
  if (typeof ms !== "number" || ms <= 0) return "…";
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

/** Show and change the status of each SL video, plus BC metadata for context. */
function SignLanguageStatus({
  eqpField,
  updateEqpField,
  signVideos,
  parentDuration,
  uploadingSignId,
  onUploadForSign,
  onDeleteSign,
  onPurgeMissingSign,
  onIngestForSign,
  onCancelUpload,
  canIngest,
  hasIngestProfile,
  progress,
  fileName,
  uploaded,
  importError,
  importErrorMessage,
}) {
  const handleStatusChange = ({ id, status }) => {
    updateEqpField((current) => ({
      ...current,
      sign: (current.sign ?? []).map((s) =>
        s.id === id ? { ...s, status } : s
      ),
    }));
  };

  const handleLabelChange = ({ id, label }) => {
    updateEqpField((current) => ({
      ...current,
      sign: (current.sign ?? []).map((s) =>
        s.id === id ? { ...s, label } : s
      ),
    }));
  };

  if (!eqpField || !Array.isArray(eqpField.sign) || eqpField.sign.length === 0) {
    return null;
  }

  return (
    <div className="mt-4">
      {eqpField.sign.map((sl) => {
        const bcVideo = signVideos?.[sl.id];
        const isMissing = bcVideo?.__missing === true;

        if (isMissing) {
          return (
            <div
              key={sl.id}
              className="mt-4 p-3 bg-red-50 border border-red-200 rounded"
            >
              <div className="flex items-start gap-3">
                <div className="w-24 h-14 rounded bg-red-100 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-red-700">
                    {sl.label} — {t("missing from Brightcove")}
                  </div>
                  <div className="mt-1 text-xs text-gray-600">
                    {t(
                      "This sign language video no longer exists in Brightcove."
                    )}{" "}
                    <span className="font-mono">{sl.id}</span>
                  </div>
                  <div className="mt-2 flex justify-end">
                    <Button
                      text={t("Remove from list")}
                      iconBefore="delete"
                      colour="white"
                      size="small"
                      onClick={() => onPurgeMissingSign(sl.id)}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        }

        const durationMs = bcVideo?.duration;
        const isNew = sl.status === trackStatus.new;
        const isUploading = uploadingSignId === sl.id;
        const durationMismatch =
          !isNew &&
          typeof durationMs === "number" &&
          typeof parentDuration === "number" &&
          Math.abs(durationMs - parentDuration) > 1000;
        const thumbnail = bcVideo?.images?.thumbnail?.src;
        return (
          <div
            key={sl.id}
            className="mt-4 p-3 bg-white border border-gray-200 rounded"
          >
            <div className="flex items-start gap-3">
              {thumbnail && (
                <SLThumbnail
                  src={thumbnail}
                  alt={`${sl.label} thumbnail`}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="grid grid-cols-5 gap-2 items-center">
                  <h3>Label:</h3>
                  <input
                    type="text"
                    defaultValue={sl.label}
                    className="col-span-2 p-2 font-semibold"
                    onBlur={(e) => {
                      if (e.target.value !== sl.label) {
                        handleLabelChange({
                          id: sl.id,
                          label: e.target.value,
                        });
                      }
                    }}
                  />
                  <div className="col-span-3">
                    {isNew ? (
                      <span className="italic text-sm text-gray-500">
                        {t("Waiting for content")}
                      </span>
                    ) : (<>
                      <h3>Status:</h3>
                      <Select
                        selected={sl.status}
                        onSelect={(value) => {
                          handleStatusChange({ id: sl.id, status: value });
                        }}
                      /></>
                    )}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                  <span>State: {bcVideo?.state ?? "…"}</span>
                  <span>Updated: {formatDate(bcVideo?.updated_at)}</span>
                  <span>Duration: {formatDuration(durationMs)}</span>
                </div>
                {durationMismatch && (
                  <div className="mt-1 text-xs text-orange-600">
                    {t("Duration differs from parent video")}
                    {` (${formatDuration(durationMs)} vs ${formatDuration(
                      parentDuration
                    )})`}
                  </div>
                )}
                <div className="mt-2 flex justify-end gap-2">
                  <Button
                    text={t("Open in BC")}
                    iconBefore="publish"
                    colour="white"
                    size="small"
                    onClick={() =>
                      window.open(
                        `https://studio.brightcove.com/products/videocloud/media/videos/${sl.id}`,
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                  />
                  {canIngest && (
                    <>
                      <Button
                        text={t("Ingest Processed Video")}
                        iconBefore="sparkles"
                        colour="blue"
                        size="small"
                        onClick={() =>
                          document
                            .getElementById(`sl-ingest-${sl.id}`)
                            ?.click()
                        }
                        disabled={!hasIngestProfile || isUploading}
                      />
                      <input
                        id={`sl-ingest-${sl.id}`}
                        type="file"
                        accept="video/*"
                        hidden
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            onIngestForSign(sl.id, e.target.files[0]);
                          }
                        }}
                      />
                    </>
                  )}
                </div>
                {canIngest && !hasIngestProfile && (
                  <div className="mt-1 text-xs text-orange-600 italic text-right">
                    {t(
                      "No sign-language ingest profile set on this account — an admin must configure one before ingest can run."
                    )}
                  </div>
                )}

                {isNew && !isUploading && (
                  <>
                    <div className="mt-3 flex justify-end">
                      <Button
                        text={t("Upload chroma content")}
                        colour="white"
                        iconBefore="save"
                        onClick={() =>
                          document
                            .getElementById(`sl-upload-${sl.id}`)
                            ?.click()
                        }
                      />
                      <input
                        id={`sl-upload-${sl.id}`}
                        type="file"
                        accept="video/*"
                        hidden
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            onUploadForSign(sl.id, e.target.files[0]);
                          }
                        }}
                      />
                    </div>
                    <div className="mt-2 flex justify-end">
                      <Button
                        text={t("Delete placeholder")}
                        iconBefore="delete"
                        colour="white"
                        size="small"
                        onClick={() => onDeleteSign(sl.id)}
                      />
                    </div>
                  </>
                )}

                {isUploading && (
                  <div className="mt-3 w-full px-4 py-2 flex flex-col text-gray-800 text-sm rounded border border-gray-400">
                    <div className="font-semibold text-gray-800">
                      {fileName}
                    </div>
                    <div className="mt-2 h-1 relative">
                      <div
                        style={{ width: `${progress}%` }}
                        className={`${importError
                          ? "bg-red-500"
                          : uploaded
                            ? "bg-equalplay-light"
                            : "bg-gray-700"
                          } h-1 absolute right-0 left-0 z-10`}
                      ></div>
                      <div className="bg-gray-200 h-1 absolute right-0 left-0"></div>
                    </div>
                    <div className="mt-2 flex text-sm justify-between text-gray-700">
                      {importError ? (
                        importErrorMessage
                      ) : uploaded ? (
                        <div>{t("Uploaded!")}</div>
                      ) : (
                        <button
                          onClick={onCancelUpload}
                          className="underline"
                        >
                          {t("Cancel")}
                        </button>
                      )}
                      <div>{importError ? "" : `${progress}%`}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
